<template>
    <div class="mb-8">
        <div class="text-left m-8 text-sm">
            <p class="mb-2">
                Please review the table below to ensure that you have completed all the necessary stages of the application form, your application cannot be submitted if any of these stages are incomplete or mandatory supporting documents have not been uploaded.
            </p>
            <p class="mb-2">
                Please note that once you have submitted your application you will no longer be able to make any edits. After submission you will be able to view and print a copy for your own records.
            </p>
            <h1 class="mx-8 text-green-600 text-lg text-left">Submission Declaration:</h1>
            <p>
                Upon submission of my application I hereby declare that the contents and statements contained in my application are true and accurate and contain no misrepresentations (direct or indirect).
            </p>
            <p>
                I further declare that I have not used any form of Artificial Intelligence tool(s) in the preparation and completion of my application and the contents of my application form are all my own work.
            </p>
        </div>

        <h1 class="mx-8 text-green-600 text-lg text-left">Submission review</h1>
        <div class="border border-gray-300 bg-gray-100 m-8 mt-4 p-8 flex flex-wrap text-left">
            <div class="w-full">
                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Section</div>
                    <div class="w-1/2 py-1">Status</div>
                </div>
                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Programme Details</div>
                    <div v-if="programmeData.length > 0" class="text-green-600 py-1 w-1/2">Completed</div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>
                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Personal Details</div>
                    <div v-if="personalDataComplete === true" class="text-green-600 py-1">Completed</div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>

                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Nationality Details</div>
                    <div v-if="nationalityDataComplete === true" class="text-green-600 py-1">Completed</div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>


                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Contact Details</div>
                    <div v-if="contactData.length > 0 && (contactData[0] !== undefined && contactData[0]['reference'] !==null)" class="text-green-600 py-1 w-1/2">Completed</div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>
                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Agent Details</div>
                    <div v-if="(applicationData.noAgent !== false)"
                         class="text-green-600 py-1 w-1/2"
                    >
                        Completed
                    </div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>
                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">English Test Results</div>
                    <div v-if="(englishTests.length > 0 && (englishTests[0] !== undefined && englishTests[0]['reference'] !==null) || applicationData.noEnglishTest)"
                         class="text-green-600 py-1 w-1/2"
                    >
                        Completed
                    </div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>

                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Education</div>
                    <div v-if="priorEducation.length > 0 && (priorEducation[0] !== undefined && priorEducation[0]['reference'] !==null)"
                         class="text-green-600 py-1 w-1/2"
                    >
                        Completed
                    </div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>

                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Employment</div>
                    <div v-if="(employmentHistory.length > 0 && (employmentHistory[0] !== undefined && employmentHistory[0]['reference'] !==null) || applicationData.no_prior_employment)"
                         class="text-green-600 py-1 w-1/2"
                    >
                        Completed
                    </div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>

                <div v-if="requireWorkExperience || requireMathsSkills" class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Work Experience and/or Mathematics Skills</div>
                    <div v-if="requireWorkExperience && !requireMathsSkills && applicationData.workExperience"
                         class="text-green-600 py-1 w-1/2"
                    >
                        Completed
                    </div>
                    <div v-else-if="requireMathsSkills && !requireWorkExperience && applicationData.mathematicsSkills"
                         class="text-green-600 py-1 w-1/2"
                    >
                        Completed
                    </div>
                    <div v-else-if="requireMathsSkills && requireWorkExperience && applicationData.workExperience && applicationData.mathematicsSkills"
                         class="text-green-600 py-1 w-1/2"
                    >
                        Completed
                    </div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>

                <div v-if="programmeChoiceType['num_references_required'] > 0">
                    <div class="border-b-2 py-1 flex">
                        <div class="w-1/2 py-1">Referees</div>
                        <div v-if="applicationData.referees.data.length === programmeChoiceType['num_references_required']" class="text-green-600 py-1 w-1/2">Completed</div>
                        <div v-else class="text-red-600 py-1">Incomplete <span class="text-black-400">({{ refereeCount }} of {{ programmeChoiceType['num_references_required'] }} submitted)</span></div>
                    </div>
                </div>

                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Personal Statement</div>
                    <div v-if="applicationData.personal_statement" class="text-green-600 py-1">Completed</div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>

                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Funding Details</div>
                    <div v-if="applicationData.method_of_funding" class="text-green-600 py-1">Completed</div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>

                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2">Supporting Documents</div>
                    <div v-if="documentation.length > 0" class=" py-1">
                        <span class="text-green-600">Completed</span> <span class="text-black-400">({{ documentation.length }} document<span v-if="documentation.length !== 1">s</span> uploaded)</span>
                    </div>
                    <div v-if="documentation.length === 0" class="text-red-600 py-1">Incomplete</div>
                </div>

                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Equal Opportunities</div>
                    <div v-if="equalOpsComplete" class="text-green-600 py-1">Completed</div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>

                <div class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Contextual Admissions</div>
                    <div v-if="contextualAdmissionsComplete" class="text-green-600 py-1">Completed</div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>

                <div v-if="applicationData['application_fee_required'] === 1" class="border-b-2 py-1 flex">
                    <div class="w-1/2 py-1">Application Fee Paid</div>
                    <div v-if="feePaid === 1" class="text-green-600 py-1">Completed</div>
                    <div v-else class="text-red-600 py-1">Incomplete</div>
                </div>
            </div>
        </div>



        <div class="w-fulls">
            <h1 class="mx-8 text-green-600 text-lg text-left">Application submission</h1>
            <div v-if="!draftApplication" class="border border-gray-300 bg-green-100 m-8 mt-4 p-8 text-left text-sm">
                <p style="color: greens;">Your application has been submitted and is under review</p>
            </div>
            <div v-else class="w-full">
                <div v-if="applicationData['application_fee_required'] === 1 && feePaid === 0"
                     class="border border-gray-300 bg-gray-100 m-8 mt-4 p-8 text-left text-sm w-full"
                >
                    <p class="font-bold">Important Information:</p>
                    <p class="mb-2">
                        Applications for Masters programmes are charged a &#163;{{ applicationData['application_fee_amount'] }} administration fee which must be paid <span class="font-bold">before the application can be submitted</span>.
                        Payments are handled by our payment provider, <span class="font-bold">Flywire</span>
                    </p>
                    <p class="font-bold my-2">Payment Process:</p>
                    <div class="p-3 bg-green-100 border border-green-300 rounded mb-4">
                        <p class="font-bold">Note:</p>
                        <p>Credit/debit card payments are processed fastest</p>
                    </div>
                    <p class="p-2 pl-4 border-b">1. Click the button below to start the payment process</p>
                    <p class="p-2 pl-4 border-b">
                        2. Once payment has been made and Flywire has notified LSHTM of your payment, you will be able to submit your
                        application
                    </p>
                    <p class="p-2 pl-4 border-b">3. The submit button will automatically appear once Flywire have notified us that your payment has been received</p>
                    <div v-if="feePaymentStatus === 'PROCESSING'" class="p-4">
                        <strong>Your payment is underway with Flywire. The application form submit button will here once we have been notified of the
                            completion of your payment</strong>. We are checking the payment status every 30 seconds
                        <p class="py-2">Should you need it again, the payment link is:</p>
                        <div v-if="paymentLink.length > 0">
                            <a target="_blank" :href="paymentLink">{{ paymentLink }}</a>
                        </div>
                    </div>
                    <div v-else>
                        <div v-if="paymentLink.length > 0" class="text-center">
                            <a :href="paymentLink" target="_blank"
                               class="my-8 p-2 btn-green bg-green-400 items-center text-white inline-block" @click="setInterval"
                            >Begin fee payment with Flywire</a>
                        </div>
                    </div>
                </div>
                <div v-else class="w-full p-8">
                    <div class="border border-gray-300 bg-gray-100 items-center flex flex-col p-8">
                        <div class="text-left">
                            <p class="p-2">
                                I declare that the statements on this form are correct. I understand that any offer of admission may be withdrawn if I
                                cannot provide documentary evidence of any statements on this form. I agree that the London School of Hygiene &amp; Tropical
                                Medicine may process the information contained in this form and retain it for statistical and administrative reasons.
                            </p>
                            <p class="p-2">
                                For the purpose of verifying the data about you held by the London School of Hygiene &amp; Tropical Medicine, we may disclose
                                your application information and supporting documents to other higher education institutions or government agencies. We may need
                                to contact your referees, previous academics or previous employers for verification purposes.
                            </p>
                            <p class="p-2">
                                I understand that if I am accepted onto a programme of study at the London School of Hygiene &amp; Tropical Medicine, the
                                School is not obliged to provide financial assistance.
                            </p>
                            <p class="p-2">
                                I understand that by submitting an application, I agree to LSHTM's Privacy Notice for Prospective Students:
                                <a target="_blank" href="https://www.lshtm.ac.uk/aboutus/organisation/data-protection/privacy-notices">https://www.lshtm.ac.uk/aboutus/organisation/data-protection/privacy-notices</a>
                            </p>
                        </div>
                        <div class="items-center">
                            <input id="declaration-check" v-model="declaration" type="checkbox" name="declaration" class="" />
                            <label for="declaration-check" class="ml-2 text-right"><strong>I understand that by ticking this box I agree to all of the above</strong>
                            </label>
                        </div>
                        <div class="items-center">
                            <div v-if="inactive_choice_err" class="w-full server-error mx-8">{{ inactive_choice_err }}</div>
                            <application-component-save-button :disabled="complete && declaration"
                                                               class="btn-green text-center"
                                                               type="submit"
                                                               @click="submitApplication()"
                            >
                                Submit Application
                                <div v-if="loading" class="lds-ring-container absolute top-10 left-4">
                                    <div class="lds-ring">
                                        <div />
                                        <div />
                                        <div />
                                        <div />
                                    </div>
                                </div>
                            </application-component-save-button>
                        </div>

                        <div v-if="serverError" class="w-full server-error mx-8">{{ serverError }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import ApplicationComponentSaveButton from '@/shared/ApplicationComponentSaveButton'

export default {
    components: {
        ApplicationComponentSaveButton,
    },
    props: {
        applicationReference: String,
        applicationData: Object,
        personalData: Object,
        contactData: Object,
        priorEducation: Object,
        employmentHistory: Object,
        programmeData: Object,
        documentation: Object,
        paymentData: Object,
        programmeChoiceType: {
            type: Object,
            default: function () {
                return {
                    application_fee: null,
                    num_references_required: 0,
                }
            },
        },
        programmeChoice: Object,
        equalOpportunities: Object,
        contextualData: Object,
        agents: Object,
        englishTests: Array,
    },
    data() {
        return {
            formDirty: false,
            loading: false,
            inactive_choice_err: '',
            serverError: '',
            successMessage: '',
            serverError2: '',
            successMessage2: '',
            sectionsComplete: false,
            contextualAdmissionsComplete: this.contextualData,
            applicationOpen: true,
            noPaymentRqd: false,
            draftApplication: this.applicationData.status === 'Draft',
            declaration: false,
            isAgentChecked: false,
            paymentLink: '',
            paymentType: 'application_fee',
            interval: undefined,
            feePaid: this.applicationData['application_fee_paid'] ?? 0,
            feePaymentStatus: this.applicationData['application_fee_status'] ?? '',
            applicationRef: this.applicationReference ?? '',
        }
    },
    computed: {
        refereeCount() {
            let count = 0

            for (const i in this.applicationData.referees.data)
            {
                if (this.applicationData.referees.data[i]['reference'] !== null)
                {
                    count++
                }
            }
            return count

        },
        equalOpsComplete() {
            const fields = ['sources_of_interest', 'ethnic_origin', 'gender_identity', 'religious_belief', 'sexual_orientation', 'criminal_conviction', 'disabilities']
            let complete = true
            for (const i in fields)
            {
                if (this.equalOpportunities[fields[i]] === undefined || this.equalOpportunities[fields[i]] === null)
                {
                    complete = false
                }
            }
            return complete
        },
        complete() {
            this.inactive_choice_err = ''
            if(this.programmeData.length > 0 &&  this.programmeChoice == undefined)
            {
                this.inactive_choice_err = 'Please re-select your programme choice'
                return
            }
            return this.programmeData.length > 0
                && this.programmeChoice !== undefined
                && this.personalData.last_name !== undefined
                && this.applicationData.noAgent !== false
                && (this.englishTests.length > 0 || this.applicationData.noEnglishTest)
                && this.priorEducation.length > 0
                && (this.employmentHistory.length > 0 || this.applicationData.no_prior_employment)
                && (
                    (this.requireWorkExperience && !this.requireMathsSkills && this.applicationData.workExperience) ||
                    (!this.requireWorkExperience && this.requireMathsSkills && this.applicationData.mathematicsSkills) ||
                    (this.requireWorkExperience && this.requireMathsSkills && this.applicationData.workExperience && this.applicationData.mathematicsSkills) ||
                    (!this.requireWorkExperience && !this.requireMathsSkills))
                && (this.applicationData.referees.data.length === this.programmeChoiceType['num_references_required'])
                && this.applicationData.personal_statement !== undefined && this.applicationData.method_of_funding !== undefined
                && this.documentation.length > 0 && this.equalOpsComplete === true && this.nationalityDataComplete === true
                && this.contextualAdmissionsComplete
                
        },
        personalDataComplete()
        {
            const fields = ['first_name', 'last_name', 'date_of_birth', 'email', 'gender_id']
            let complete = true
            for (const i in fields)
            {
                if (this.personalData[fields[i]] === undefined || this.personalData[fields[i]] === null)
                {
                    complete = false
                }
            }
            return complete
        },
        nationalityDataComplete()
        {
            const fields = ['nationality_id', 'country_of_birth_id', 'country_of_domicile_id']
            let complete = true
            for (const i in fields)
            {
                if (this.applicationData[fields[i]] === undefined || this.applicationData[fields[i]] === null)
                {
                    complete = false
                }
            }
            return complete
        },
        requireWorkExperience() {
            if (this.programmeData === undefined || this.programmeData === null)
                return false

            let filtered = this.programmeData.filter(programme => programme.requireWorkExperience)
            return filtered !== undefined && filtered !== null && filtered.length > 0
        },
        requireMathsSkills() {
            if (this.programmeData === undefined || this.programmeData === null)
                return false

            let filtered = this.programmeData.filter(programme => programme.requireMathsSkills)
            return filtered !== undefined && filtered !== null && filtered.length > 0
        },
    },

    mounted() {
        this.$nextTick(function () {
            this.getLink()
        })

        if (this.applicationData['application_fee_required'] === 1 && this.applicationData['application_fee_paid'] === 0)
        {
            if (this.feePaymentStatus === 'PROCESSING')
            {
                this.setInterval()
            }
        }
    },
    methods: {
        clearInterval() {
            clearInterval(this.interval)
        },
        setInterval() {
            setTimeout(() => {
                this.feePaymentStatus = 'PROCESSING'
                this.checkPaymentStatus()
            }, 2000)


            this.interval = setInterval(() => {
                this.checkPaymentStatus()
            },30000)
        },

        checkPaymentStatus() {
            this.$store.dispatch('checkPaymentStatus', {
                applicationRef: this.applicationRef,
                paymentType: this.paymentType,
            })
                .then(response => {
                    if (response.data === '1')
                    {
                        this.feePaid = 1
                        clearInterval(this.interval)
                    }
                })
                .catch(() => {
                    clearInterval(this.interval)
                })
        },
        getLink() {
            this.$store.dispatch('getPaymentLink', {
                applicationRef: this.applicationRef,
                paymentType: this.paymentType,
            })
                .then((response) => {
                    this.paymentLink = response.data
                })
                .catch(() => {
                    this.paymentLink = ''
                })
        },
        dirtyForm() {
            this.formDirty = true
            this.successMessage = ''
        },

        submitApplication() {
            if(this.complete && this.declaration)
            {
            }
            else
            {
                alert('You need to complete all sections')
                return
            }
            this.loading = true
            if(confirm('Are you sure you want to submit your application?')){

                this.$store.dispatch('submitApplication', {
                    applicationRef: this.applicationRef,
                    agent: this.agent,
                })
                    .then(response => {
                        this.draftApplication = false
                        this.successMessage = response.data
                        setTimeout(() => {
                            location.reload()
                        }, 5000) // 5000 milliseconds = 5 seconds
                    })
                    .catch(error => {
                        if (error.response) {
                            this.serverError = error.response.data
                        }
                    })
            }
        },
    },
}
</script>